import { useTranslation } from 'react-i18next';
import { useInvitationStoreSelector } from '@/contexts/invitation-context';
import type { InvitationChannelMode } from '@/stores/invitation-store';
import { DeliveryBanner } from './outreach/delivery-banner';

interface InvitationDeliveryBannerProps {
  mode: InvitationChannelMode;
  /** Back to the channel step; the composed body survives in the store. */
  onChangeChannel: () => void;
  /**
   * Bulk recipient count (items WITH an email, in both modes). When set the
   * banner shows a count badge and the bulk sublines instead of a mono avatar.
   */
  count?: number;
}

/** One-clause subline describing the picked delivery channel (mono). */
const SUBLINE_KEY: Record<InvitationChannelMode, string> = {
  link: 'invitations.modal.channel.banner.link',
  email: 'invitations.modal.channel.banner.email',
  dm: 'invitations.modal.channel.banner.dm',
  comment: 'invitations.modal.channel.banner.comment',
};

/** Bulk sublines: only link and email apply (no DM/comment on bulk). */
const BULK_SUBLINE_KEY: Partial<Record<InvitationChannelMode, string>> = {
  link: 'invitations.modal.channel.banner.bulkLink',
  email: 'invitations.modal.channel.banner.bulkEmail',
};

/**
 * Recipient banner heading the affiliation delivery step, shared across modes
 * and the mono/bulk shapes. Mono: the recipient name (or raw email) is the
 * title, an avatar leads, the subline names the mode. Bulk: a recipient-count
 * badge leads, the title is the count label and the subline is the bulk mode.
 * "Change" returns to the channel step (the composed body survives in the
 * store). A thin wrapper over the shared {@link DeliveryBanner}.
 */
export const InvitationDeliveryBanner = ({
  mode,
  onChangeChannel,
  count,
}: InvitationDeliveryBannerProps) => {
  const { t } = useTranslation();
  const creator = useInvitationStoreSelector((state) => state.creator);
  const collabRecipientDisplay = useInvitationStoreSelector(
    (state) => state.collabRecipientDisplay
  );
  const target = useInvitationStoreSelector((state) => state.target);

  const isBulk = count !== undefined;

  if (isBulk) {
    return (
      <DeliveryBanner
        count={count}
        title={t('invitations.modal.channel.bulk.recipients', { count })}
        subline={t(BULK_SUBLINE_KEY[mode] ?? BULK_SUBLINE_KEY.link!)}
        changeLabel={t('invitations.modal.channel.banner.change')}
        onChange={onChangeChannel}
      />
    );
  }

  // Title: the recipient's display name when one is known, else the raw email
  // address. The avatar falls back to initials of that title when no picture.
  const title =
    creator?.name ??
    collabRecipientDisplay?.displayName ??
    (target?.type === 'affiliate' ? target.data.name : undefined) ??
    (target?.type === 'email' ? target.data.email : undefined) ??
    '';
  const avatarSrc = creator?.profilePictureUrl ?? collabRecipientDisplay?.avatarUrl ?? null;

  return (
    <DeliveryBanner
      avatar={{ name: title, src: avatarSrc }}
      title={title}
      subline={t(SUBLINE_KEY[mode])}
      changeLabel={t('invitations.modal.channel.banner.change')}
      onChange={onChangeChannel}
    />
  );
};
